import type {
  CollectDataTemplateStepsConfig,
  OnboardingTemplate,
  OnboardingTemplateStep,
} from 'src/interfaces/onboardingTemplate'
import {
  isFlowEditorNewConfigId,
  normalizeCollectDataTemplateConfigId,
  resolveStepGroupConfigId,
} from 'src/utils/collectDataTemplateEditor/configIds'
import {
  flowEditorGroupAddStepDisabled,
  flowEditorGroupHasInsuranceCoverageSteps,
} from 'src/utils/collectDataTemplateEditor/insuranceSync'
import {
  getFlowEditorEmptyStepGroupSaveMessage,
  buildFlowEditorSidebarOrderedConfigIds,
} from 'src/utils/collectDataTemplateEditor/sidebarGroups'
import {
  getFlowEditorFormStepSaveMessage,
  getFlowEditorLmsTrainingSaveMessage,
  getFlowEditorSignContractSaveMessage,
  getFlowEditorYardstikPackageSaveMessage,
} from 'src/utils/collectDataTemplateEditor/saveValidation'
import type { FlowEditorStepGroupFieldOverrides } from 'src/utils/collectDataTemplateEditor/types'
import type { FlowEditorTemplateDocument } from 'src/utils/flowEditorDocument/types'
import { FLOW_EDITOR_FEATURE_FLAGS } from 'src/constants/flowEditorFeatureFlags'
import { stepLabel } from 'src/constants/collect-data/stepTypeLabels'
import { buildGroupSettingsFromTemplateConfigs } from './flowEditorStateUtils'

export interface FlowEditorSidebarGroup {
  addStepDisabled: boolean
  stepsConfig: CollectDataTemplateStepsConfig | undefined
  configId: string
  displayDelayed: boolean
  displayLabel: string
  groupOrdinal: number
  groupSteps: OnboardingTemplateStep[]
  isEmptyPlaceholder: boolean
}

export function computeFlowEditorIsDirty(params: {
  emptyGroupUiIds: string[]
  flowName: string
  groupSettingsById: Record<string, FlowEditorStepGroupFieldOverrides>
  savedSnapshot: OnboardingTemplate | null
  savedSnapshotEditorSteps: OnboardingTemplateStep[]
  steps: OnboardingTemplateStep[]
}): boolean {
  const {
    emptyGroupUiIds,
    flowName,
    groupSettingsById,
    savedSnapshot,
    savedSnapshotEditorSteps,
    steps,
  } = params

  if (!savedSnapshot) {
    return false
  }

  if (emptyGroupUiIds.length > 0) {
    return true
  }

  if (flowName !== savedSnapshot.name) {
    return true
  }

  if (steps.length !== savedSnapshotEditorSteps.length) {
    return true
  }

  const savedOrder = savedSnapshotEditorSteps.map((step) => step.id).join(',')
  const currentOrder = steps.map((step) => step.id).join(',')

  if (savedOrder !== currentOrder) {
    return true
  }

  if (
    JSON.stringify(steps.map((step) => step.config)) !==
    JSON.stringify(savedSnapshotEditorSteps.map((step) => step.config))
  ) {
    return true
  }

  const savedByIdForDirty = new Map(savedSnapshotEditorSteps.map((step) => [step.id, step]))

  for (const step of steps) {
    const savedStep = savedByIdForDirty.get(step.id)

    if (savedStep && step.retroactiveCollectRequired !== savedStep.retroactiveCollectRequired) {
      return true
    }

    if (savedStep && step.ignoreMonitoring !== savedStep.ignoreMonitoring) {
      return true
    }

    if (savedStep && step.recollectSameTypeSteps !== savedStep.recollectSameTypeSteps) {
      return true
    }

    if (
      savedStep &&
      step.skipRetroactiveCollectIfAnySameTypeCompleted !==
        savedStep.skipRetroactiveCollectIfAnySameTypeCompleted
    ) {
      return true
    }

    if (savedStep && step.collectDataTemplateStepLabel !== savedStep.collectDataTemplateStepLabel) {
      return true
    }
  }

  const savedGroupFields = buildGroupSettingsFromTemplateConfigs(
    savedSnapshot.collectDataTemplateStepsConfigs,
  )
  const groupKeys = new Set([...Object.keys(groupSettingsById), ...Object.keys(savedGroupFields)])

  for (const groupId of Array.from(groupKeys)) {
    const currentGroupSettings = groupSettingsById[groupId]
    const savedGroupSettings = savedGroupFields[groupId]

    if (currentGroupSettings && savedGroupSettings) {
      if (
        currentGroupSettings.label !== savedGroupSettings.label ||
        currentGroupSettings.delayed !== savedGroupSettings.delayed ||
        (currentGroupSettings.insurancePolicyId ?? null) !==
          (savedGroupSettings.insurancePolicyId ?? null)
      ) {
        return true
      }

      continue
    }

    if (currentGroupSettings && !savedGroupSettings) {
      return true
    }

    if (!currentGroupSettings && savedGroupSettings) {
      return true
    }
  }

  return false
}

export function buildFlowEditorSidebarGroups(params: {
  emptyGroupUiIds: string[]
  groupSettingsById: Record<string, FlowEditorStepGroupFieldOverrides>
  steps: OnboardingTemplateStep[]
  template: OnboardingTemplate
}): FlowEditorSidebarGroup[] {
  const { emptyGroupUiIds, groupSettingsById, steps, template } = params
  const configs = template.collectDataTemplateStepsConfigs ?? []
  const configById = new Map(
    configs.map((stepsConfig) => [
      normalizeCollectDataTemplateConfigId(stepsConfig.id) || stepsConfig.id,
      stepsConfig,
    ]),
  )
  const orderedIds = buildFlowEditorSidebarOrderedConfigIds(template, steps, emptyGroupUiIds)

  return orderedIds.map((configId, groupIndex) => {
    const stepsConfig = configById.get(configId)
    const groupSteps = steps.filter((step) => resolveStepGroupConfigId(step, template) === configId)
    const isEmptyPlaceholder = emptyGroupUiIds.some(
      (emptyGroupId) =>
        (normalizeCollectDataTemplateConfigId(emptyGroupId) || emptyGroupId) === configId,
    )
    const groupSettings = groupSettingsById[configId]
    const fallbackLabel =
      (stepsConfig?.label && stepsConfig.label.trim() !== '' ? stepsConfig.label : null) ??
      (groupSteps[0] ? stepLabel(groupSteps[0].type) : null) ??
      'Step group'
    const groupSettingsLabelTrimmed = groupSettings?.label?.trim() ?? ''
    const displayLabel =
      groupSettingsLabelTrimmed !== '' ? groupSettingsLabelTrimmed : fallbackLabel
    const displayDelayed = groupSettings?.delayed ?? stepsConfig?.delayed ?? false
    const insurancePolicyId =
      groupSettings?.insurancePolicyId ?? stepsConfig?.insurancePolicyId ?? null
    const addStepDisabled = flowEditorGroupAddStepDisabled(
      steps,
      template,
      configId,
      insurancePolicyId,
    )

    return {
      addStepDisabled,
      stepsConfig,
      configId,
      displayDelayed,
      displayLabel,
      groupOrdinal: groupIndex + 1,
      groupSteps,
      isEmptyPlaceholder,
    }
  })
}

/**
 * True when a group the create request would carry has a blank name.
 *
 * `buildActionsFromDocumentDiff` sends `label` verbatim and only for groups that hold a step, and
 * the create DTO marks it as required — so a blank one comes back as an opaque 400.
 */
function hasBlankFlowEditorStepGroupLabel(draft: FlowEditorTemplateDocument | null): boolean {
  if (!draft) {
    return false
  }

  return draft.configLocalIds.some((configLocalId) => {
    const config = draft.configsByLocalId[configLocalId]

    if (!config || config.stepLocalIds.length === 0) {
      return false
    }

    return (config.label ?? '').trim() === ''
  })
}

export function computeFlowEditorSaveBlockedMessage(params: {
  draft: FlowEditorTemplateDocument | null
  emptyGroupUiIds: string[]
  flowName: string
  isCreateMode: boolean
  steps: OnboardingTemplateStep[]
  template: OnboardingTemplate | null
}): string | null {
  const { draft, emptyGroupUiIds, flowName, isCreateMode, steps, template } = params

  if (!template) {
    return null
  }

  // New-template rules run first: the create endpoint requires a name, at least one step group,
  // and at least one step in every group it is sent. Blocking here shows the reason instead of
  // letting the request come back 400.
  if (isCreateMode) {
    if (flowName.trim() === '') {
      return 'Template name is required.'
    }

    if (steps.length === 0) {
      return 'Add at least one step group with a step before saving.'
    }

    // Create-mode only on purpose: the edit/PUT path has the same hole today, and blocking a save
    // it currently permits would change production behaviour outside this feature's scope.
    if (hasBlankFlowEditorStepGroupLabel(draft)) {
      return 'Every step group needs a name.'
    }
  }

  return (
    getFlowEditorEmptyStepGroupSaveMessage(template, steps, emptyGroupUiIds) ??
    getFlowEditorYardstikPackageSaveMessage(steps) ??
    getFlowEditorLmsTrainingSaveMessage(steps) ??
    getFlowEditorSignContractSaveMessage(steps) ??
    getFlowEditorFormStepSaveMessage(steps)
  )
}

export interface SelectedGroupPanelProps {
  canMoveDown: boolean
  canMoveUp: boolean
  canRemoveGroup: boolean
  delayed: boolean
  groupOrdinal: number
  insurancePolicyId: string | null
  label: string
  showInsurancePolicySelector: boolean
  totalGroups: number
}

export function buildSelectedGroupPanelProps(params: {
  flowEditorSidebarGroups: FlowEditorSidebarGroup[]
  groupSettingsById: Record<string, FlowEditorStepGroupFieldOverrides>
  selectedGroupConfigId: string | null
  steps: OnboardingTemplateStep[]
  template: OnboardingTemplate | null
}): SelectedGroupPanelProps | null {
  const { flowEditorSidebarGroups, groupSettingsById, selectedGroupConfigId, steps, template } =
    params

  if (!selectedGroupConfigId || !template) {
    return null
  }

  const groupIndex = flowEditorSidebarGroups.findIndex(
    (sidebarGroup) => sidebarGroup.configId === selectedGroupConfigId,
  )

  if (groupIndex < 0) {
    return null
  }

  const sidebarGroup = flowEditorSidebarGroups[groupIndex]
  const groupSettings = groupSettingsById[selectedGroupConfigId]
  const allow = FLOW_EDITOR_FEATURE_FLAGS.allowReorderSteps

  const insurancePolicyId: string | null =
    groupSettings !== undefined
      ? (() => {
          const policyId = groupSettings.insurancePolicyId

          if (policyId === null) {
            return null
          }

          const trimmedPolicyId = policyId.trim()

          return trimmedPolicyId === '' ? null : trimmedPolicyId
        })()
      : (() => {
          const serverPolicyId = sidebarGroup.stepsConfig?.insurancePolicyId

          if (typeof serverPolicyId !== 'string' || serverPolicyId.trim() === '') {
            return null
          }

          return serverPolicyId.trim()
        })()

  const showInsurancePolicySelector =
    flowEditorGroupHasInsuranceCoverageSteps(steps, template, selectedGroupConfigId) ||
    isFlowEditorNewConfigId(selectedGroupConfigId, template)

  return {
    canMoveDown: allow && groupIndex < flowEditorSidebarGroups.length - 1,
    canMoveUp: allow && groupIndex > 0,
    canRemoveGroup: sidebarGroup.isEmptyPlaceholder || FLOW_EDITOR_FEATURE_FLAGS.allowDeleteSteps,
    delayed: groupSettings?.delayed ?? sidebarGroup.displayDelayed,
    groupOrdinal: sidebarGroup.groupOrdinal,
    insurancePolicyId,
    label: groupSettings?.label ?? sidebarGroup.displayLabel,
    showInsurancePolicySelector,
    totalGroups: flowEditorSidebarGroups.length,
  }
}
